import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { ManagementService } from '../../services/management.service';
import * as ManagementActions from '../actions/management.actions';
import * as userActions from '../actions/user.actions';

@Injectable()
export class ManagementEffects {
  private actions$ = inject(Actions);
  private managementService = inject(ManagementService);
  private toastr = inject(ToastrService);
  private reconciledOrgIds = new Set<string>();


  /** e.g. 'District "Kumbanad District"'; falls back to "unit" when the level is unknown. */
  private nodeLabel({ levelName, nodeName }: ManagementActions.NodeLabel): string {
    const level = levelName?.trim() || 'Unit';
    return nodeName?.trim() ? `${level} "${nodeName.trim()}"` : level;
  }
  constructor() {}

  loadMyAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.loadMyAssignments),
      switchMap(({ userId }) =>
        this.managementService.getMyManagementAssignments(userId).pipe(
          map((assignments) =>
            ManagementActions.loadMyAssignmentsSuccess({ assignments })
          ),
          catchError((error) =>
            of(ManagementActions.loadMyAssignmentsFailure({ error }))
          )
        )
      )
    )
  );

  loadOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.loadOrganization),
      switchMap(({ orgId }) =>
        this.managementService.getOrganization(orgId).pipe(
          map((organization) =>
            ManagementActions.loadOrganizationSuccess({ organization })
          ),
          catchError((error) =>
            of(ManagementActions.loadOrganizationFailure({ error }))
          )
        )
      )
    )
  );

  loadHierarchyNodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.loadHierarchyNodes),
      tap(({ orgId }) => {
        // One-off repair of legacy assignments: run at most once per org per session,
        // not on every reload triggered by a mutation
        if (orgId && !this.reconciledOrgIds.has(orgId)) {
          this.reconciledOrgIds.add(orgId);
          this.managementService.reconcileLegacyAssignments(orgId);
        }
      }),
      switchMap(({ orgId }) =>
        this.managementService.getHierarchyNodes(orgId).pipe(
          map((nodes) => ManagementActions.loadHierarchyNodesSuccess({ nodes })),
          catchError((error) =>
            of(ManagementActions.loadHierarchyNodesFailure({ error }))
          )
        )
      )
    )
  );

  loadAffiliatedChurches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.loadAffiliatedChurches),
      switchMap(({ orgId }) =>
        this.managementService.getAffiliatedChurches(orgId).pipe(
          map((churches) =>
            ManagementActions.loadAffiliatedChurchesSuccess({ churches })
          ),
          catchError((error) =>
            of(ManagementActions.loadAffiliatedChurchesFailure({ error }))
          )
        )
      )
    )
  );

  loadNodeLeaders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.loadNodeLeaders),
      switchMap(({ orgId, nodeId }) =>
        this.managementService.getNodeLeaders(orgId, nodeId).pipe(
          map((leaders) =>
            ManagementActions.loadNodeLeadersSuccess({ leaders })
          ),
          catchError((error) =>
            of(ManagementActions.loadNodeLeadersFailure({ error }))
          )
        )
      )
    )
  );

  loadChurchOversight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.loadChurchOversight),
      switchMap(({ churchId }) =>
        this.managementService.getChurchOversight(churchId).pipe(
          map((oversight) =>
            ManagementActions.loadChurchOversightSuccess(oversight)
          ),
          catchError((error) =>
            of(ManagementActions.loadChurchOversightFailure({ error }))
          )
        )
      )
    )
  );

  createHierarchyNode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.createHierarchyNode),
      mergeMap(({ orgId, node }) => {
        const label = { levelName: node.levelName, nodeName: node.name };
        return this.managementService.createHierarchyNode(node).then(
          (nodeId) =>
            ManagementActions.createHierarchyNodeSuccess({ orgId, nodeId, ...label }),
          (error) => ManagementActions.createHierarchyNodeFailure({ error, ...label })
        );
      })
    )
  );

  updateHierarchyNode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.updateHierarchyNode),
      mergeMap(({ orgId, nodeId, updates, levelName, nodeName }) =>
        this.managementService.updateHierarchyNode(nodeId, updates).then(
          () =>
            ManagementActions.updateHierarchyNodeSuccess({ orgId, nodeId, levelName, nodeName }),
          (error) => ManagementActions.updateHierarchyNodeFailure({ error, levelName, nodeName })
        )
      )
    )
  );

  deleteHierarchyNode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.deleteHierarchyNode),
      mergeMap(({ orgId, nodeId, parentNodeId, levelName, nodeName }) =>
        this.managementService.deleteHierarchyNode(nodeId).then(
          () =>
            ManagementActions.deleteHierarchyNodeSuccess({
              orgId,
              nodeId,
              parentNodeId,
              levelName,
              nodeName,
            }),
          (error) => ManagementActions.deleteHierarchyNodeFailure({ error, levelName, nodeName })
        )
      )
    )
  );

  reloadAfterNodeMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ManagementActions.createHierarchyNodeSuccess,
        ManagementActions.updateHierarchyNodeSuccess,
        ManagementActions.deleteHierarchyNodeSuccess
      ),
      switchMap(({ orgId }) => [
        ManagementActions.loadHierarchyNodes({ orgId }),
        ManagementActions.loadAffiliatedChurches({ orgId }),
      ])
    )
  );

  removeNodeLeader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.removeNodeLeader),
      mergeMap(({ orgId, nodeId, assignmentId }) =>
        this.managementService.removeNodeLeader(assignmentId).then(
          () => ManagementActions.removeNodeLeaderSuccess({ orgId, nodeId }),
          (error) => ManagementActions.removeNodeLeaderFailure({ error })
        )
      )
    )
  );

  reloadAfterLeaderMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ManagementActions.assignNodeLeaderSuccess,
        ManagementActions.removeNodeLeaderSuccess
      ),
      switchMap(({ orgId, nodeId }) =>
        of(ManagementActions.loadNodeLeaders({ orgId, nodeId }))
      )
    )
  );

  initMyOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.authenticated, userActions.loadUserSuccess),
      map(({ user }) => user?.uid || ''),
      filter((uid) => !!uid),
      // loadUserSuccess fires on every profile refresh; only re-subscribe when the user changes
      distinctUntilChanged(),
      map((userId) => ManagementActions.loadMyOrganizations({ userId }))
    )
  );

  updateOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.updateOrganization),
      mergeMap(({ orgId, updates }) =>
        this.managementService
          .updateOrganization(orgId, updates)
          .then(() =>
            ManagementActions.updateOrganizationSuccess({ orgId, updates })
          )
          .catch((error) =>
            ManagementActions.updateOrganizationFailure({ error })
          )
      )
    )
  );

  reloadOrgAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.updateOrganizationSuccess),
      switchMap(({ orgId }) => of(ManagementActions.loadOrganization({ orgId })))
    )
  );

  loadMyOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.loadMyOrganizations),
      switchMap(({ userId }) =>
        this.managementService.getMyOrganizations(userId).pipe(
          map((organizations) =>
            ManagementActions.loadMyOrganizationsSuccess({ organizations })
          ),
          catchError((error) =>
            of(ManagementActions.loadMyOrganizationsFailure({ error }))
          )
        )
      )
    )
  );

  loadOrganizationMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.loadOrganizationMembers),
      switchMap(({ orgId }) =>
        this.managementService.getOrganizationMembers(orgId).pipe(
          map((members) =>
            ManagementActions.loadOrganizationMembersSuccess({ members })
          ),
          catchError((error) =>
            of(ManagementActions.loadOrganizationMembersFailure({ error }))
          )
        )
      )
    )
  );

  inviteOrganizationMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.inviteOrganizationMember),
      mergeMap(({ orgId, email, name, role }) =>
        this.managementService
          .inviteMember(orgId, email, name, role)
          .then((member) =>
            ManagementActions.inviteOrganizationMemberSuccess({ member })
          )
          .catch((error) =>
            ManagementActions.inviteOrganizationMemberFailure({ error })
          )
      )
    )
  );

  requestOrganizationMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.requestOrganizationMembership),
      mergeMap(({ orgId, user }) =>
        this.managementService
          .requestMembership(orgId, user)
          .then(({ member, outcome }) =>
            ManagementActions.requestOrganizationMembershipSuccess({ member, outcome })
          )
          .catch((error) =>
            ManagementActions.requestOrganizationMembershipFailure({ error })
          )
      )
    )
  );

  updateOrganizationMemberStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.updateOrganizationMemberStatus),
      mergeMap(({ orgId, memberDocId, status, role }) =>
        this.managementService
          .updateMemberStatus(memberDocId, status, role)
          .then(() =>
            ManagementActions.updateOrganizationMemberStatusSuccess({
              orgId,
              memberDocId,
              status,
              role,
            })
          )
          .catch((error) =>
            ManagementActions.updateOrganizationMemberStatusFailure({ error })
          )
      )
    )
  );

  loadOrganizationAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.loadOrganizationAssignments),
      switchMap(({ orgId }) =>
        this.managementService.getOrganizationAssignments(orgId).pipe(
          map((assignments) =>
            ManagementActions.loadOrganizationAssignmentsSuccess({ assignments })
          ),
          catchError((error) =>
            of(ManagementActions.loadOrganizationAssignmentsFailure({ error }))
          )
        )
      )
    )
  );

  assignMemberToNodeRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.assignMemberToNodeRole),
      mergeMap(({ orgId, nodeId, roleId, member }) =>
        this.managementService
          .assignMemberToNodeRole(orgId, nodeId, roleId, member)
          .then(() =>
            ManagementActions.assignMemberToNodeRoleSuccess({ orgId, nodeId })
          )
          .catch((error) =>
            ManagementActions.assignMemberToNodeRoleFailure({ error })
          )
      )
    )
  );

  removeMemberFromNodeRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.removeMemberFromNodeRole),
      mergeMap(({ orgId, nodeId, roleId, userId }) =>
        this.managementService
          .removeMemberFromNodeRole(orgId, nodeId, roleId, userId)
          .then(() =>
            ManagementActions.removeMemberFromNodeRoleSuccess({ orgId, nodeId })
          )
          .catch((error) =>
            ManagementActions.removeMemberFromNodeRoleFailure({ error })
          )
      )
    )
  );

  reloadAfterRoleAssignmentMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ManagementActions.assignMemberToNodeRoleSuccess,
        ManagementActions.removeMemberFromNodeRoleSuccess
      ),
      switchMap(({ orgId }) => [
        ManagementActions.loadOrganizationAssignments({ orgId }),
        ManagementActions.loadHierarchyNodes({ orgId }),
      ])
    )
  );

  moveChurchAffiliation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.moveChurchAffiliation),
      mergeMap(({ orgId, churchId, targetNodeId }) =>
        this.managementService
          .moveChurchAffiliation(orgId, churchId, targetNodeId)
          .then(() =>
            ManagementActions.moveChurchAffiliationSuccess({
              orgId,
              churchId,
              targetNodeId,
            })
          )
          .catch((error) =>
            ManagementActions.moveChurchAffiliationFailure({ error })
          )
      )
    )
  );

  reloadAfterChurchMove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementActions.moveChurchAffiliationSuccess),
      switchMap(({ orgId }) => [
        ManagementActions.loadAffiliatedChurches({ orgId }),
        ManagementActions.loadHierarchyNodes({ orgId }),
      ])
    )
  );

  notifyMutations$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ManagementActions.createHierarchyNodeSuccess,
          ManagementActions.createHierarchyNodeFailure,
          ManagementActions.updateHierarchyNodeSuccess,
          ManagementActions.updateHierarchyNodeFailure,
          ManagementActions.deleteHierarchyNodeSuccess,
          ManagementActions.deleteHierarchyNodeFailure,
          ManagementActions.assignNodeLeaderSuccess,
          ManagementActions.assignNodeLeaderFailure,
          ManagementActions.removeNodeLeaderSuccess,
          ManagementActions.removeNodeLeaderFailure,
          ManagementActions.inviteOrganizationMemberSuccess,
          ManagementActions.inviteOrganizationMemberFailure,
          ManagementActions.requestOrganizationMembershipSuccess,
          ManagementActions.requestOrganizationMembershipFailure,
          ManagementActions.updateOrganizationMemberStatusSuccess,
          ManagementActions.updateOrganizationMemberStatusFailure,
          ManagementActions.updateOrganizationSuccess,
          ManagementActions.updateOrganizationFailure,
          ManagementActions.moveChurchAffiliationSuccess,
          ManagementActions.moveChurchAffiliationFailure
        ),
        tap((action) => {
          switch (action.type) {
            case ManagementActions.createHierarchyNodeSuccess.type:
              this.toastr.success(`${this.nodeLabel(action as any)} created.`);
              break;
            case ManagementActions.createHierarchyNodeFailure.type:
              this.toastr.error(
                `Failed to create ${this.nodeLabel(action as any)}: ` +
                  ((action as any).error?.message || 'Permission or network error')
              );
              break;
            case ManagementActions.updateHierarchyNodeSuccess.type:
              this.toastr.success(`${this.nodeLabel(action as any)} renamed.`);
              break;
            case ManagementActions.updateHierarchyNodeFailure.type:
              this.toastr.error(
                `Failed to rename ${this.nodeLabel(action as any)}: ` +
                  ((action as any).error?.message || 'Permission or network error')
              );
              break;
            case ManagementActions.deleteHierarchyNodeSuccess.type:
              this.toastr.success(`${this.nodeLabel(action as any)} deleted.`);
              break;
            case ManagementActions.deleteHierarchyNodeFailure.type:
              this.toastr.error(
                `Failed to delete ${this.nodeLabel(action as any)}: ` +
                  ((action as any).error?.message || 'Permission or network error')
              );
              break;
            case ManagementActions.assignNodeLeaderSuccess.type:
              this.toastr.success('Leader assigned successfully.');
              break;
            case ManagementActions.assignNodeLeaderFailure.type:
              this.toastr.error(
                'Failed to assign leader: ' +
                  ((action as any).error?.message || 'Permission or network error')
              );
              break;
            case ManagementActions.removeNodeLeaderSuccess.type:
              this.toastr.success('Leader assignment removed.');
              break;
            case ManagementActions.removeNodeLeaderFailure.type:
              this.toastr.error(
                'Failed to remove leader: ' +
                  ((action as any).error?.message || 'Permission or network error')
              );
              break;
            case ManagementActions.inviteOrganizationMemberSuccess.type:
              this.toastr.success('Member invited successfully.');
              break;
            case ManagementActions.inviteOrganizationMemberFailure.type:
              this.toastr.error(
                'Failed to invite member: ' +
                  ((action as any).error?.message || 'Error occurred')
              );
              break;
            case ManagementActions.requestOrganizationMembershipSuccess.type:
              switch ((action as ReturnType<typeof ManagementActions.requestOrganizationMembershipSuccess>).outcome) {
                case 'joined':
                  this.toastr.success('Invitation accepted. You are now a member.');
                  break;
                case 'already_member':
                  this.toastr.info('You are already a member of this organization.');
                  break;
                case 'already_requested':
                  this.toastr.info('Your request to join is already awaiting review.');
                  break;
                default:
                  this.toastr.success('Request to join submitted successfully.');
              }
              break;
            case ManagementActions.requestOrganizationMembershipFailure.type:
              this.toastr.error(
                'Failed to submit request: ' +
                  ((action as any).error?.message || 'Error occurred')
              );
              break;
            case ManagementActions.updateOrganizationMemberStatusSuccess.type:
              this.toastr.success('Member status updated successfully.');
              break;
            case ManagementActions.updateOrganizationMemberStatusFailure.type:
              this.toastr.error(
                'Failed to update member status: ' +
                  ((action as any).error?.message || 'Error occurred')
              );
              break;
            case ManagementActions.updateOrganizationSuccess.type:
              this.toastr.success('Organization settings updated successfully.');
              break;
            case ManagementActions.updateOrganizationFailure.type:
              this.toastr.error(
                'Failed to update organization: ' +
                  ((action as any).error?.message || 'Error occurred')
              );
              break;
            case ManagementActions.moveChurchAffiliationSuccess.type:
              this.toastr.success('Church jurisdiction updated successfully.');
              break;
            case ManagementActions.moveChurchAffiliationFailure.type:
              this.toastr.error(
                'Failed to move church: ' +
                  ((action as any).error?.message || 'Error occurred')
              );
              break;
          }
        })
      ),
    { dispatch: false }
  );
}
